import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { MsalProvider } from '@azure/msal-react'
import { PublicClientApplication } from '@azure/msal-browser'
import App from './App'
import { currentEnvironment, msalConfig } from './config/authConfig'
import { refreshCacheForNewBuild } from './services/cacheService'
import * as cm from './services/connectionManager'
import {
  clearUnexpectedTenantAccounts,
  rememberSuccessfulLogin,
  resolvePreferredAccount,
  setAuthError,
} from './services/authSession'
import './index.css'

// Auto-reload once when a deploy invalidates old chunk filenames.
// The catch-all _redirects serves index.html for missing .js files,
// which the browser rejects as text/html for a module import.
// Skip during MSAL redirect callbacks to avoid destroying auth state.
window.addEventListener('vite:preloadError', () => {
  const hasAuthResponse = window.location.hash.includes('code=') ||
    window.location.hash.includes('error=') ||
    window.location.search.includes('code=') ||
    window.location.search.includes('error=')
  if (hasAuthResponse) return

  const key = 'orbit-chunk-reload'
  if (!sessionStorage.getItem(key)) {
    sessionStorage.setItem(key, '1')
    window.location.reload()
  }
})

const msalInstance = new PublicClientApplication(msalConfig)
bootstrap()

async function bootstrap() {
  await msalInstance.initialize()
  const redirectResult = await msalInstance.handleRedirectPromise()

  // If running inside a hidden iframe (e.g. ssoSilent callback), MSAL has already
  // communicated the tokens to the parent window via handleRedirectPromise.
  // Do NOT render the full app or attempt further auth — just stop here.
  if (window.self !== window.top) return

  if (redirectResult?.account) {
    if ((redirectResult.account.tenantId || '').toLowerCase() !== currentEnvironment.tenantId.toLowerCase()) {
      setAuthError(`The selected Microsoft account authenticated against tenant ${redirectResult.account.tenantId || 'unknown'} instead of ${currentEnvironment.tenantId}. Please sign in with your ${currentEnvironment.tenant === 'ICG' ? 'ICG work account' : 'expected work account'}.`)
      await msalInstance.clearCache({ account: redirectResult.account }).catch(() => {})
      msalInstance.setActiveAccount(null)
    } else {
      msalInstance.setActiveAccount(redirectResult.account)
      rememberSuccessfulLogin(redirectResult.account)
    }
  } else {
    await clearUnexpectedTenantAccounts(msalInstance)
    const preferredAccount = resolvePreferredAccount(msalInstance, msalInstance.getAllAccounts())
    if (preferredAccount) {
      msalInstance.setActiveAccount(preferredAccount)
      rememberSuccessfulLogin(preferredAccount)
    }
  }

  cm.initialize(msalInstance)
  void cm.warmUp()
  void refreshCacheForNewBuild()

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </MsalProvider>
    </React.StrictMode>,
  )
}
